import React, { Suspense, useEffect } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ThemeProvider } from '@material-ui/core/styles';
import Link from '@material-ui/core/Link';

import './SupplierSearchApp.css';
import theme from 'config/theme';
import { PRODUCT_TYPE } from 'constants/product';
import { LoggedInPersonProvider, useLoggedInPerson } from 'hooks/person/useLoggedInPerson';
import { ProductContextProvider } from 'hooks/product/useProductContext';
import { useQueryParameters } from 'hooks/url/useQueryParameters';
import { SearchContextProvider } from 'hooks/search/useSearchContext';
import ActivityIndicator from 'components/activity/ActivityIndicator';
import FilterPanel from 'components/panel/filter/FilterPanel';
import Footer from 'components/footer/Footer';
import FullScreenLayout from 'components/layout/FullScreenLayout';
import Header from 'components/header/Header';
import LayoutMatch from 'components/layout/LayoutMatch';
import LayoutSwitch from 'components/layout/LayoutSwitch';
import MaskedAppDialog from 'components/dialog/MaskedAppDialog';
import ProductMatch from 'components/product/ProductMatch';
import ProductSwitch from 'components/product/ProductSwitch';
import SnackbarProvider from 'components/snackbar/SnackbarProvider';
import StartUpNotification from 'components/notification/StartUpNotification';
import APIConfig from 'config/api';

// lazy loaded components (code splitting)
const BidTeamPanel = React.lazy(() => import('components/panel/supplier/sourcing/BidTeamPanel'));
const BidTeamSearchPanel = React.lazy(() => import('components/panel/search/sourcing/BidTeamSearchPanel'));
const DiscoveryLayout = React.lazy(() => import('components/layout/DiscoveryLayout'));
const DiscoverySearchPanel = React.lazy(() => import('components/panel/search/discovery/DiscoverySearchPanel'));
const GlobalSupplierListPanel = React.lazy(() => import('components/panel/supplier/discovery/GlobalSupplierListPanel'));
const MainLayout = React.lazy(() => import('components/layout/MainLayout'));
const SearchModePanel = React.lazy(() => import('components/panel/SearchModePanel'));
const SinglePanelLayout = React.lazy(() => import('components/layout/SinglePanelLayout'));
const SupplierGroupPanel = React.lazy(() => import('components/panel/supplier/portal/SupplierGroupPanel'));
const SupplierGroupSearchPanel = React.lazy(() => import('components/panel/search/portal/SupplierGroupSearchPanel'));

//
// --- Helper Components ---

const AppLoading = () => (
  <div className="supplier-search-app" data-testid="supplier-search-app">
    <FullScreenLayout>
      <ThemeProvider theme={theme}>
        <ActivityIndicator message="Loading supplier search ..." />
      </ThemeProvider>
    </FullScreenLayout>
  </div>
);

const SourcingStartUpNotificationMessage = React.memo(({ offeringKey }) => (
  <div>
    <div>
      <Link href={`/setupBidTeams.do?okey=${offeringKey}`} color="secondary">
        The old Supplier Setup page
      </Link>
    </div>
    is still available until Feb 28, 2022
  </div>
));

//
// --- App Component ---

/**
 * App is a "helper" component used internally in the main SupplierSearchApp component.
 * This component requires a logged in person object to be set before it will
 * render any additional content in the application.
 */
const App = () => {
  const { error, isLoading, isViewOnly, person, personCompany } = useLoggedInPerson();
  const {
    contractId,
    gridWidgetId,
    gridWidgetInitialWidth,
    gridWidgetType,
    isDiscovery,
    offeringKey,
    portalId,
    productType,
    searchTerm
  } = useQueryParameters();
  const [isAppMaximized, setIsAppMaximized] = React.useState(false);
  const [isLoadingSupplierSearch, setIsLoadingSupplierSearch] = React.useState(true);
  const [useNewSupplierSearch, setUseNewSupplierSearch] = React.useState(false);

  const isSourcingProductType = productType === PRODUCT_TYPE.SOURCING;

  useEffect(() => {
    getSupplierSearchFlag();
  }, [person]);

  const getSupplierSearchFlag = async () => {
    if (person) {
      const useNewSupplierSearchResponse = await axios.request(
        APIConfig.SupplierSearch.getNewSupplierSearchFeatureToggleFlag(person.personkey)
      );
      setIsLoadingSupplierSearch(false);
      setUseNewSupplierSearch(useNewSupplierSearchResponse.data);
    } else {
      setIsLoadingSupplierSearch(false);
    }
  };

  if (isLoading || isLoadingSupplierSearch) {
    return <AppLoading />;
  }

  if (error) {
    return <div data-testid="supplier-search-app-error">{error}</div>;
  }

  if (!person || !(isDiscovery || contractId || offeringKey || portalId)) {
    return null;
  }

  // Temporary notification for linking back to old sourcing page
  // Automatically stops showing notification at configured time
  const isSourcingStartUpNotificationEnabled = Date.now() < new Date('2022-03-01T00:00:00.000-05:00').getTime();

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <SearchContextProvider isDiscovery={isDiscovery} personCompany={personCompany} searchTerm={searchTerm}>
          <ProductContextProvider personCompany={personCompany} personKey={person.personkey}>
            <CssBaseline />
            <div
              className={clsx('supplier-search-app', { 'supplier-search-app-iframe': isDiscovery })}
              data-testid="supplier-search-app"
            >
              {!isDiscovery && <Header isMinimized={isAppMaximized} />}
              <div className="supplier-search-app-content-container">
                <Suspense fallback={<AppLoading />}>
                  <LayoutSwitch currentProduct={productType} isDiscovery={isDiscovery}>
                    <LayoutMatch product={[PRODUCT_TYPE.MARKET, PRODUCT_TYPE.SOURCING]} isDiscovery>
                      <DiscoveryLayout
                        leftPanel={
                          <FilterPanel
                            buyerCompanyKey={person.companykey}
                            buyerCompanyName={personCompany.companyName}
                            hideContactNameFilter
                            hideGlobalFilterSearchPanel
                            hideSupplierRequestPanel
                            useNewSupplierSearch={useNewSupplierSearch}
                            isDiscovery={isDiscovery}
                          />
                        }
                        middlePanel={
                          <DiscoverySearchPanel
                            buyerCompanyKey={person.companykey}
                            useNewSupplierSearch={useNewSupplierSearch}
                          />
                        }
                        rightPanel={<GlobalSupplierListPanel />}
                        widgetId={gridWidgetId}
                        widgetInitialColumnWidth={gridWidgetInitialWidth}
                        widgetType={gridWidgetType}
                      />
                    </LayoutMatch>
                    <LayoutMatch
                      isDiscovery={false}
                      product={[PRODUCT_TYPE.CONTRACT, PRODUCT_TYPE.PORTAL, PRODUCT_TYPE.SOURCING]}
                    >
                      {isViewOnly ? (
                        <SinglePanelLayout
                          isMaximized={isAppMaximized}
                          onMaximizeButtonClick={() => setIsAppMaximized(!isAppMaximized)}
                          productType={productType}
                          panel={
                            <ProductSwitch currentProduct={productType}>
                              <ProductMatch product={PRODUCT_TYPE.CONTRACT}>future contract panel</ProductMatch>
                              <ProductMatch product={PRODUCT_TYPE.PORTAL}>
                                <SupplierGroupPanel hideEditControls />
                              </ProductMatch>
                              <ProductMatch product={PRODUCT_TYPE.SOURCING}>
                                <BidTeamPanel
                                  hideEditControls
                                  isStatusFinalized
                                  loggedInPerson={person}
                                  offeringKey={offeringKey}
                                />
                              </ProductMatch>
                            </ProductSwitch>
                          }
                        />
                      ) : (
                        <MainLayout
                          isMaximized={isAppMaximized}
                          leftHeaderPanel={
                            <SearchModePanel
                              showBidEventItemListMenuItem={productType === PRODUCT_TYPE.SOURCING}
                              useNewSupplierSearch={useNewSupplierSearch}
                            />
                          }
                          leftPanel={
                            <FilterPanel
                              buyerCompanyKey={person.companykey}
                              buyerCompanyName={personCompany.companyName}
                              hideSupplierRequestPanel={!isSourcingProductType}
                              useNewSupplierSearch={useNewSupplierSearch}
                              isDiscovery={isDiscovery}
                            />
                          }
                          middlePanel={
                            <ProductSwitch currentProduct={productType}>
                              <ProductMatch product={PRODUCT_TYPE.SOURCING}>
                                <BidTeamSearchPanel loggedInPerson={person} />
                              </ProductMatch>
                              <ProductMatch product={PRODUCT_TYPE.PORTAL}>
                                <SupplierGroupSearchPanel />
                              </ProductMatch>
                            </ProductSwitch>
                          }
                          onMaximizeButtonClick={() => setIsAppMaximized(!isAppMaximized)}
                          productType={productType}
                          rightPanel={
                            <ProductSwitch currentProduct={productType}>
                              <ProductMatch product={PRODUCT_TYPE.CONTRACT}>future contract panel</ProductMatch>
                              <ProductMatch product={PRODUCT_TYPE.PORTAL}>
                                <SupplierGroupPanel />
                              </ProductMatch>
                              <ProductMatch product={PRODUCT_TYPE.SOURCING}>
                                <BidTeamPanel offeringKey={offeringKey} loggedInPerson={person} />
                              </ProductMatch>
                            </ProductSwitch>
                          }
                        />
                      )}
                    </LayoutMatch>
                  </LayoutSwitch>
                  {isSourcingStartUpNotificationEnabled && !isDiscovery && productType === PRODUCT_TYPE.SOURCING && (
                    <StartUpNotification
                      delay={3000}
                      message={<SourcingStartUpNotificationMessage offeringKey={offeringKey} />}
                      snackbarOptions={{ persist: true, variant: 'info' }}
                    />
                  )}
                </Suspense>
                <MaskedAppDialog />
              </div>
              {!isDiscovery && <Footer />}
            </div>
          </ProductContextProvider>
        </SearchContextProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

//
// --- SupplierSearchApp Component ---

/**
 * Provide access to logged in person so App knows if it should render or not.
 * This allows us to use the useLoggedInPerson hook in the App component
 */
const SupplierSearchApp = () => (
  <LoggedInPersonProvider>
    <App />
  </LoggedInPersonProvider>
);

export default SupplierSearchApp;
